import { ENTITY_TYPE, API_RESPONSE_TYPE } from '../../constants'
import { ROLE } from '../../state/transaction/transaction.types'
import { getMerchantTransactionsApi } from '../Api/StafflinkApi'
import { getTransactionDatabaseManager } from '../IndexDB/TransactionDatabaseManager'
import { AuthStorageInstance } from '../Storage/AuthStorage'
import { Mixpanel } from '../Tracking/Mixpanel'

// The delta is requested from a little BEFORE the cursor. A max-`update_time` cursor
// alone permanently skips any row the server commits late with an older timestamp
// (and the boundary second itself, since `start_time` is exclusive). Re-fetched rows
// upsert by `id` and aren't counted as changes, so the overlap is free.
const CURSOR_OVERLAP_SECONDS = 5 * 60

export interface IRefreshResult {
  success: boolean
  /** total delta transactions applied across customers + suppliers */
  count: number
  customerCount: number
  supplierCount: number
  /** true when one account type synced but the other failed */
  partial?: boolean
  error?: string
}

/**
 * Decode a FILE-response `file_data` payload (signed URL + AES key) with the
 * lazy-loaded shared decoder — the same path SimpleSmartSync uses. Returns null
 * (caller treats it as "no delta this refresh") when the decoder is unsupported,
 * the payload is incomplete, or the decode fails. Never throws.
 */
async function tryDecodeFileData(fileData: any): Promise<any[] | null> {
  try {
    const url = fileData?.file
    const encryptionKey = fileData?.encryption_key
    if (!url || !encryptionKey) {
      return null
    }
    const decoderMod = await import(/* webpackChunkName: "txn-seed" */ './decoder')
    if (!decoderMod.isDecoderSupported()) {
      return null
    }
    return await decoderMod.downloadAndDecodeTransactionFile({
      url,
      encryption_key: encryptionKey
    })
  } catch (err) {
    console.warn('[RefreshMerchant] FILE decode failed:', err)
    return null
  }
}

/**
 * Refresh the delta for a single account type. Reads that type's business-wide
 * `update_time` cursor, makes ONE `type:0` GetTransactions call (no role =
 * customers/seller side; `role:2` = suppliers/buyer side), and merge-upserts the
 * delta into IndexedDB by `id`. Returns the number of delta rows applied.
 * Throws on a failed API response so the caller can report a partial refresh.
 */
async function refreshEntityType(
  dbManager: any,
  entityType: ENTITY_TYPE,
  businessId?: string
): Promise<number> {
  const cursor: number = await dbManager.getBusinessMaxServerUpdateTime(entityType, businessId)
  const startTime = Math.max(0, cursor - CURSOR_OVERLAP_SECONDS)
  const role = entityType === ENTITY_TYPE.SUPPLIER ? ROLE.SUPPLIER : undefined
  console.log('[RefreshMerchant] Delta refresh', entityType, 'from start_time', startTime)

  const response = await getMerchantTransactionsApi({ start_time: startTime, role })
  if (response.type !== API_RESPONSE_TYPE.SUCCESS) {
    throw new Error(`GetTransactions (${entityType}) request failed`)
  }

  let delta: any[] | null = response.data?.list_data?.transactions ?? null

  // Large delta → backend returns an encrypted file instead of an inline list.
  if (!delta && response.data?.file_data) {
    delta = await tryDecodeFileData(response.data.file_data)
    if (!delta) {
      // Looks identical to "no new transactions" from here on, so make it visible:
      // a merchant whose delta always comes back as a file would never sync.
      Mixpanel.track('txn_delta_file_decode_failed', {
        entity_type: entityType === ENTITY_TYPE.CUSTOMER ? 'customer' : 'supplier',
        start_time: startTime
      })
    }
  }

  if (!delta || delta.length === 0) {
    console.log('[RefreshMerchant] No new', entityType, 'transactions')
    return 0
  }

  const applied = await dbManager.bulkUpsertTransactions(entityType, delta, businessId)
  console.log('[RefreshMerchant] Applied', applied, entityType, 'transactions')
  return applied
}

/**
 * Incremental pull-to-refresh for the active business's transactions. By default
 * refreshes BOTH customer and supplier ledgers (Android parity), but callers can
 * pass a subset — e.g. a `customer_txns` push only needs the customer side, which
 * halves the work per push. Each account type uses its own `update_time` cursor
 * and its own `type:0` call. Types run concurrently; a failure on one side still
 * commits the other and reports `partial: true`.
 */
export async function refreshMerchantTransactions(
  entityTypes: ENTITY_TYPE[] = [ENTITY_TYPE.CUSTOMER, ENTITY_TYPE.SUPPLIER]
): Promise<IRefreshResult> {
  try {
    const dbManager = getTransactionDatabaseManager()
    await dbManager.init()

    // Same source the API layer uses for the OKC-Business-Id header, so the cursor
    // and the rows it tags always belong to the business the delta is fetched for.
    const businessId: string | undefined =
      (await AuthStorageInstance.getCurrentBusinessId()) || undefined

    const results = await Promise.allSettled(
      entityTypes.map((t) => refreshEntityType(dbManager, t, businessId))
    )

    let customerCount = 0
    let supplierCount = 0
    entityTypes.forEach((t, i) => {
      const r = results[i]
      const value = r.status === 'fulfilled' ? r.value : 0
      if (t === ENTITY_TYPE.SUPPLIER) {
        supplierCount = value
      } else {
        customerCount = value
      }
    })
    const rejected = results.filter((r) => r.status === 'rejected')

    // Every requested side failed → surface a hard failure.
    if (rejected.length === entityTypes.length && entityTypes.length > 0) {
      const reason = (rejected[0] as PromiseRejectedResult).reason
      console.error('[RefreshMerchant] Refresh failed:', reason)
      return {
        success: false,
        count: 0,
        customerCount: 0,
        supplierCount: 0,
        error: reason instanceof Error ? reason.message : 'Unknown error'
      }
    }

    return {
      success: true,
      count: customerCount + supplierCount,
      customerCount,
      supplierCount,
      partial: rejected.length > 0
    }
  } catch (error) {
    console.error('[RefreshMerchant] Refresh failed:', error)
    return {
      success: false,
      count: 0,
      customerCount: 0,
      supplierCount: 0,
      error: error instanceof Error ? error.message : 'Unknown error'
    }
  }
}
